import {
  ArchiveBoxIcon,
  ChevronRightIcon,
  CloudIcon,
  FolderIcon,
  FolderPlusIcon,
  GlobeAltIcon,
  MagnifyingGlassIcon,
  StarIcon
} from '@heroicons/react/24/outline';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getCurrentUser } from '../../data/auth';
import {
  createFolder,
  describeLocation,
  GraphError,
  getMyDrive,
  isOneDrive,
  listDrives,
  listFolders,
  locationFolderId,
  ONEDRIVE_SITE_ID,
  searchSites
} from '../../data/graph';
import { acquireGraphTokenSilently, connectGraph } from '../../data/graphAuth';
import { describeApiError } from '../../data/http';
import { addSharePointFavourite, listSharePointFavourites, removeSharePointFavourite } from '../../data/sharePointFavourites';
import type { SharePointDrive, SharePointFavourite, SharePointFolder, SharePointLocation } from '../../data/types';

interface Position {
  site: { id: string; name: string } | null;
  drive: SharePointDrive | null;
  folders: SharePointFolder[];
}

interface Row {
  id: string;
  name: string;
}

interface Listed {
  key: string;
  rows: Row[];
  error: string | null;
}

interface LocationPickerProps {
  initialLocation?: SharePointLocation | null;
  onChange: (location: SharePointLocation | null) => void;
  /** Off for shared destinations such as a template's location: a personal OneDrive differs per person. */
  allowOneDrive?: boolean;
}

const rowButton =
  'flex w-full items-center gap-2 rounded px-3 py-2 text-left text-sm text-foreground transition hover:bg-muted focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring';

const secondaryButton =
  'inline-flex items-center justify-center gap-2 rounded border border-input bg-surface px-3 py-2 text-sm font-semibold text-foreground transition hover:bg-muted focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring disabled:cursor-not-allowed disabled:opacity-60';

const inputClass =
  'w-full rounded-sm border border-input bg-surface px-3 py-2 text-sm outline-none transition focus:border-ring focus:ring-2 focus:ring-ring/22';

function toPosition(location: SharePointLocation | null | undefined): Position {
  if (!location) return { site: null, drive: null, folders: [] };
  return {
    site: { id: location.siteId, name: location.siteName },
    drive: { id: location.driveId, name: location.driveName },
    folders: location.folders
  };
}

function toLocation(position: Position): SharePointLocation | null {
  if (!position.site || !position.drive) return null;
  return {
    siteId: position.site.id,
    siteName: position.site.name,
    driveId: position.drive.id,
    driveName: position.drive.name,
    folders: position.folders
  };
}

function isSameLocation(a: SharePointLocation, b: SharePointLocation): boolean {
  return a.driveId === b.driveId && locationFolderId(a) === locationFolderId(b);
}

/** Free browsing of whatever the signed-in user can reach: site, library, then folders. */
export function LocationPicker({ initialLocation, onChange, allowOneDrive = true }: LocationPickerProps) {
  const { t } = useTranslation();

  const [auth, setAuth] = useState<'checking' | 'ready' | 'needed'>('checking');
  const [loginHint, setLoginHint] = useState<string | undefined>();
  const [connectError, setConnectError] = useState<string | null>(null);

  const [position, setPosition] = useState<Position>(() => toPosition(initialLocation));
  const [search, setSearch] = useState('');
  const [activeSearch, setActiveSearch] = useState('');
  const [listed, setListed] = useState<Listed | null>(null);

  const [favourites, setFavourites] = useState<SharePointFavourite[]>([]);
  const [newFolderName, setNewFolderName] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    getCurrentUser()
      .then(async (user) => {
        if (!cancelled) setLoginHint(user?.userDetails);
        return acquireGraphTokenSilently(user?.userDetails);
      })
      .then((token) => {
        if (!cancelled) setAuth(token ? 'ready' : 'needed');
      })
      .catch(() => {
        if (!cancelled) setAuth('needed');
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    // Favourites are a convenience, so a failure to load them must not block browsing.
    listSharePointFavourites()
      .then((loaded) => {
        if (!cancelled) setFavourites(loaded);
      })
      .catch(() => undefined);

    return () => {
      cancelled = true;
    };
  }, []);

  const siteId = position.site?.id ?? null;
  const driveId = position.drive?.id ?? null;
  const parentId = position.folders.at(-1)?.id ?? null;
  const level = !siteId ? 'sites' : !driveId ? 'drives' : 'folders';
  const listKey = `${level}|${siteId}|${driveId}|${parentId}|${activeSearch}`;

  useEffect(() => {
    if (auth !== 'ready') return;
    let cancelled = false;

    const load = async (): Promise<Row[]> => {
      if (level === 'sites') return searchSites(activeSearch);
      if (level === 'drives') return listDrives(siteId ?? '');
      return listFolders(driveId ?? '', parentId);
    };

    load()
      .then((rows) => {
        if (!cancelled) setListed({ key: listKey, rows, error: null });
      })
      .catch((error: unknown) => {
        if (!cancelled) setListed({ key: listKey, rows: [], error: describeApiError(error, t('sharePoint.picker.loadFailed')) });
      });

    return () => {
      cancelled = true;
    };
  }, [auth, level, siteId, driveId, parentId, activeSearch, listKey, t]);

  const loading = listed?.key !== listKey;
  const rows = loading ? [] : (listed?.rows ?? []);
  const listError = loading ? null : (listed?.error ?? null);

  const move = (next: Position) => {
    setPosition(next);
    setNewFolderName(null);
    setActionError(null);
    onChange(toLocation(next));
  };

  const open = (row: Row) => {
    if (level === 'sites') move({ site: row, drive: null, folders: [] });
    else if (level === 'drives') move({ ...position, drive: row, folders: [] });
    else move({ ...position, folders: [...position.folders, row] });
  };

  const connect = () => {
    setConnectError(null);
    connectGraph(loginHint)
      .then(() => setAuth('ready'))
      .catch((error: unknown) => setConnectError(describeApiError(error, t('sharePoint.picker.connectFailed'))));
  };

  const openOneDrive = () => {
    setActionError(null);
    getMyDrive()
      .then((drive) => move({ site: { id: ONEDRIVE_SITE_ID, name: t('sharePoint.picker.oneDrive') }, drive, folders: [] }))
      .catch((error: unknown) =>
        setActionError(
          error instanceof GraphError && error.status === 404
            ? t('sharePoint.picker.oneDriveUnavailable')
            : describeApiError(error, t('sharePoint.picker.oneDriveUnavailable'))
        )
      );
  };

  const submitSearch = (event: React.SubmitEvent<HTMLFormElement>) => {
    event.preventDefault();
    setActiveSearch(search);
  };

  const submitFolder = (event: React.SubmitEvent<HTMLFormElement>) => {
    event.preventDefault();
    const name = newFolderName?.trim();
    if (!name || !driveId) return;

    createFolder(driveId, parentId, name)
      .then((created) => move({ ...position, folders: [...position.folders, created] }))
      .catch((error: unknown) => setActionError(describeApiError(error, t('sharePoint.picker.createFolderFailed'))));
  };

  const current = toLocation(position);
  const currentFavourite = current ? favourites.find((favourite) => isSameLocation(favourite, current)) : undefined;

  const toggleFavourite = () => {
    if (!current) return;
    setActionError(null);

    const change = currentFavourite
      ? removeSharePointFavourite(currentFavourite.favouriteId).then(() =>
          setFavourites((existing) => existing.filter((favourite) => favourite.favouriteId !== currentFavourite.favouriteId))
        )
      : addSharePointFavourite(current).then((added) => setFavourites((existing) => [...existing, added]));

    change.catch((error: unknown) => setActionError(describeApiError(error, t('sharePoint.picker.favouriteFailed'))));
  };

  if (auth === 'checking') {
    return (
      <p className="text-sm text-muted-foreground" aria-live="polite">
        {t('common.loading')}
      </p>
    );
  }

  if (auth === 'needed') {
    return (
      <div className="flex flex-col items-start gap-3">
        <p className="text-sm text-muted-foreground">{t('sharePoint.picker.connectBody')}</p>
        <button
          type="button"
          onClick={connect}
          className="inline-flex items-center justify-center gap-2 rounded bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground ring-1 ring-primary-hover transition hover:bg-primary-hover focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
        >
          {t('sharePoint.picker.connect')}
        </button>
        {connectError ? (
          <p role="alert" className="text-sm text-destructive">
            {connectError}
          </p>
        ) : null}
      </div>
    );
  }

  const Icon = level === 'sites' ? GlobeAltIcon : level === 'drives' ? ArchiveBoxIcon : FolderIcon;
  const shownFavourites = allowOneDrive ? favourites : favourites.filter((favourite) => !isOneDrive(favourite));
  const inOneDrive = position.site !== null && position.site.id === ONEDRIVE_SITE_ID;

  return (
    <div className="flex flex-col gap-4">
      {shownFavourites.length > 0 ? (
        <section aria-label={t('sharePoint.picker.favourites')}>
          <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{t('sharePoint.picker.favourites')}</h3>
          <ul className="mt-1 flex flex-wrap gap-2">
            {shownFavourites.map((favourite) => (
              <li key={favourite.favouriteId}>
                <button
                  type="button"
                  onClick={() => move(toPosition(favourite))}
                  className="inline-flex items-center gap-1.5 rounded border border-input bg-surface px-2.5 py-1.5 text-xs font-semibold text-foreground transition hover:bg-muted focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                >
                  <StarIcon className="size-3.5 fill-current" aria-hidden="true" />
                  {describeLocation(favourite)}
                </button>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <nav aria-label={t('sharePoint.picker.breadcrumb')} className="flex flex-wrap items-center gap-1 text-sm">
        <button
          type="button"
          onClick={() => move({ site: null, drive: null, folders: [] })}
          className="font-semibold text-foreground underline-offset-2 hover:underline"
        >
          {t('sharePoint.picker.sites')}
        </button>
        {position.site ? (
          <>
            <ChevronRightIcon className="size-3.5 text-muted-foreground" aria-hidden="true" />
            <button
              type="button"
              onClick={() => (inOneDrive ? move({ ...position, folders: [] }) : move({ site: position.site, drive: null, folders: [] }))}
              className="font-semibold text-foreground underline-offset-2 hover:underline"
            >
              {position.site.name}
            </button>
          </>
        ) : null}
        {position.drive && !inOneDrive ? (
          <>
            <ChevronRightIcon className="size-3.5 text-muted-foreground" aria-hidden="true" />
            <button
              type="button"
              onClick={() => move({ ...position, folders: [] })}
              className="font-semibold text-foreground underline-offset-2 hover:underline"
            >
              {position.drive.name}
            </button>
          </>
        ) : null}
        {position.folders.map((folder, index) => (
          <span key={folder.id} className="inline-flex items-center gap-1">
            <ChevronRightIcon className="size-3.5 text-muted-foreground" aria-hidden="true" />
            <button
              type="button"
              onClick={() => move({ ...position, folders: position.folders.slice(0, index + 1) })}
              className="font-semibold text-foreground underline-offset-2 hover:underline"
            >
              {folder.name}
            </button>
          </span>
        ))}
      </nav>

      {level === 'sites' ? (
        <form onSubmit={submitSearch} className="flex gap-2">
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t('sharePoint.picker.searchPlaceholder')}
            aria-label={t('sharePoint.picker.searchPlaceholder')}
            className={inputClass}
          />
          <button type="submit" className={secondaryButton}>
            <MagnifyingGlassIcon className="size-4" aria-hidden="true" />
            {t('sharePoint.picker.search')}
          </button>
        </form>
      ) : null}

      {level === 'sites' && allowOneDrive ? (
        <button type="button" onClick={openOneDrive} className={`${rowButton} border border-border`}>
          <CloudIcon className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
          <span>{t('sharePoint.picker.oneDrive')}</span>
        </button>
      ) : null}

      {listError ? (
        <p role="alert" className="border-l-4 border-destructive bg-destructive-soft px-4 py-3 text-sm text-destructive">
          {listError}
        </p>
      ) : null}

      {loading ? (
        <p className="text-sm text-muted-foreground" aria-live="polite">
          {t('common.loading')}
        </p>
      ) : rows.length === 0 && !listError ? (
        <p className="text-sm text-muted-foreground">{t(`sharePoint.picker.empty.${level}`)}</p>
      ) : (
        <ul className="max-h-64 overflow-y-auto rounded border border-border">
          {rows.map((row) => (
            <li key={row.id}>
              <button type="button" onClick={() => open(row)} className={rowButton}>
                <Icon className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                <span className="truncate">{row.name}</span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {level === 'folders' ? (
        <div className="flex flex-wrap items-center gap-2">
          {newFolderName === null ? (
            <button type="button" onClick={() => setNewFolderName('')} className={secondaryButton}>
              <FolderPlusIcon className="size-4" aria-hidden="true" />
              {t('sharePoint.picker.newFolder')}
            </button>
          ) : (
            <form onSubmit={submitFolder} className="flex flex-1 gap-2">
              <input
                value={newFolderName}
                onChange={(event) => setNewFolderName(event.target.value)}
                placeholder={t('sharePoint.picker.folderName')}
                aria-label={t('sharePoint.picker.folderName')}
                className={inputClass}
                autoFocus
              />
              <button type="submit" disabled={!newFolderName.trim()} className={secondaryButton}>
                {t('sharePoint.picker.createFolder')}
              </button>
              <button type="button" onClick={() => setNewFolderName(null)} className={secondaryButton}>
                {t('common.cancel')}
              </button>
            </form>
          )}
          <button
            type="button"
            onClick={toggleFavourite}
            aria-pressed={currentFavourite !== undefined}
            className={secondaryButton}
          >
            <StarIcon className={`size-4 ${currentFavourite ? 'fill-current' : ''}`} aria-hidden="true" />
            {currentFavourite ? t('sharePoint.picker.unfavourite') : t('sharePoint.picker.favourite')}
          </button>
        </div>
      ) : null}

      {actionError ? (
        <p role="alert" className="text-sm text-destructive">
          {actionError}
        </p>
      ) : null}
    </div>
  );
}
